import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField";
import { InputBaseComponentProps } from "@mui/material/InputBase";
import NumberFormat, { NumberFormatProps } from "react-number-format";
import parse from "date-fns/parse";

function dollarsToCents(amount: string): number {
  const [whole, fraction = ""] = amount.split(".");
  const cents = (fraction + "00").slice(0, 2);
  return parseInt(whole, 10) * 100 + parseInt(cents, 10);
}

interface AmountInputProps {
  onChange: (event: { target: { value: string } }) => void;
}

const AmountInput = React.forwardRef<NumberFormatProps, AmountInputProps>(function AmountInput(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
      decimalScale={2}
      allowNegative={false}
    />
  );
});

export interface RecordNoticePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: { paidAmountCents: number; paidAt: Date }) => void | Promise<void>;
  entityLabel: string;
  isSubmitting?: boolean;
}

interface FormValues {
  amount: string;
  paidAt: string;
}

const validationSchema = yup.object({
  amount: yup
    .string()
    .required("Amount is required")
    .test("positive", "Amount must be positive", (value) => {
      if (!value) {
        return false;
      }
      return parseFloat(value) > 0;
    }),
  paidAt: yup
    .string()
    .required("Paid at is required")
    .test("not-future", "Date cannot be in the future", (value) => {
      if (!value) {
        return true;
      }
      return value <= getCurrentDateLocal();
    }),
});

function getCurrentDateLocal(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

export const RecordNoticePaymentDialog: React.FC<RecordNoticePaymentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  entityLabel,
  isSubmitting = false,
}) => {
  const initialValues: FormValues = {
    amount: "",
    paidAt: getCurrentDateLocal(),
  };

  const handleClose = (formik: { resetForm: () => void }) => {
    formik.resetForm();
    onClose();
  };

  const handleSubmit = (values: FormValues) => {
    const paidAmountCents = dollarsToCents(values.amount);
    const paidAt = parse(values.paidAt, "yyyy-MM-dd", new Date());
    return onSubmit({ paidAmountCents, paidAt });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record {entityLabel} Payment</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {(formik) => (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="amount"
                    label="Amount ($)"
                    fullWidth
                    value={formik.values.amount}
                    onChange={(e) => formik.setFieldValue("amount", e.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.amount && Boolean(formik.errors.amount)}
                    helperText={formik.touched.amount && formik.errors.amount}
                    InputProps={{
                      inputComponent: AmountInput as unknown as React.ElementType<InputBaseComponentProps>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="paidAt"
                    label="Paid At"
                    type="date"
                    fullWidth
                    inputProps={{ max: getCurrentDateLocal() }}
                    InputLabelProps={{ shrink: true }}
                    error={formik.touched.paidAt && Boolean(formik.errors.paidAt)}
                    helperText={formik.touched.paidAt && formik.errors.paidAt}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button fullWidth onClick={() => handleClose(formik)}>
                Cancel
              </Button>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                loading={isSubmitting || formik.isSubmitting}
                disabled={isSubmitting || formik.isSubmitting || !formik.dirty || !formik.isValid}
              >
                Record Payment
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default RecordNoticePaymentDialog;
